import classNames from 'classnames';
import React, {useEffect} from 'react';
import {Field, reduxForm} from 'redux-form';

import {translate} from '../../../services';
import {CommentObject} from '../../../shapes/comments';
import {onClickFormPreventDefault} from '../../../utils/events';
import {renderTextarea} from '../../../utils/form';
import {isFunction} from '../../../utils/validation';
import Icon from '../../Reusable/Icon/Icon';


export interface CommentsFormData {
  comment?: string;
}

export interface CommentsFormProps {
  handleSubmit?: () => void;
  reset?: () => void;
  error?: Record<string, unknown>;
  onClickReturn: (updatedCommentId: number | undefined) => void;
  commentId?: number;
  isReply?: boolean;
  comments?: CommentObject[];
}

const CommentsForm = ({
  handleSubmit, onClickReturn, commentId, isReply, comments, reset,
  error
}: CommentsFormProps): JSX.Element => {
  useEffect(() => {
    isFunction(reset) && reset();
  }, [comments]); // eslint-disable-line

  const onClickSubmitComment = () => {
    const updatedCommentId = isReply ? commentId : undefined;

    return onClickReturn(updatedCommentId);
  };

  const submitForm = (event: {preventDefault: () => void}) => {
    onClickFormPreventDefault(event);
    handleSubmit && handleSubmit();
  };

  const placeholder = isReply ? translate('designs.comments.addReply') : translate('designs.comments.addComment');
  const label = isReply ? translate('designs.comments.reply') : translate('designs.comments.addComment');
  const buttonTitle = isReply ? translate('designs.comments.submitReply') : translate('designs.comments.submitComment');
  const buttonText = isReply ? translate('designs.comments.postReply') : translate('designs.comments.postComment');

  const formClasses = classNames('b-form', {
    'x-reply-form': isReply,
    'x-error': error
  });

  const fieldsetClassName = classNames({
    'comment-fieldset': !isReply,
    'reply-fieldset': isReply
  });

  const fieldClassName = classNames({
    'comment-field': !isReply,
    'reply-text-field': isReply
  });

  return (
    <form className={formClasses} onSubmit={submitForm}>
      <fieldset className={fieldsetClassName}>
        <legend className='visuallyhidden'>{label}</legend>
        <div className='fieldset-wrapper'>
          {isReply && <Icon iconName='arrow-return' iconTitle={translate('designs.comments.reply')}/>}
          <div className='field-wrapper'>
            <Field
              name='comment'
              className={fieldClassName}
              component={renderTextarea}
              formPrefix={'comments'}
              placeholder={placeholder}
              label={label}
              labelClass='visuallyhidden'/>
            <button
              type='submit'
              className='btn blue-button comment-reply-btn'
              aria-label={buttonTitle}
              title={buttonTitle}
              onClick={onClickSubmitComment}>
              {buttonText}
            </button>
          </div>
        </div>
      </fieldset>
    </form>
  );
};

// Decorate with redux-form
export default reduxForm<Record<string, unknown>, CommentsFormProps>({
  form: 'comments'
})(CommentsForm);
